import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Table, Badge, Form, Spinner } from 'react-bootstrap';
import { Bar, Doughnut } from 'react-chartjs-2';
import auditLogService from '../../services/auditLogService';

/**
 * 감사로그 통계 뷰
 */
const AuditLogStatsView = ({ filters, searchTrigger }) => {
  const [loading, setLoading] = useState(false);
  const [categoryStats, setCategoryStats] = useState([]);
  const [timelineStats, setTimelineStats] = useState([]);
  const [userActivityStats, setUserActivityStats] = useState([]);
  const [timeInterval, setTimeInterval] = useState('day');

  useEffect(() => {
    fetchAllStats();
  }, [filters, timeInterval, searchTrigger]);

  const fetchAllStats = async () => {
    setLoading(true);
    try {
      const cleanFilters = Object.fromEntries(
        Object.entries(filters).filter(([_, value]) => value !== '')
      );

      // 카테고리 통계
      const categoryResult = await auditLogService.getCategoryStats(cleanFilters);

      if (categoryResult.success) {
        setCategoryStats(categoryResult.data || []);
      }

      // 시간대별 통계
      const timelineResult = await auditLogService.getTimelineStats(timeInterval, cleanFilters);
      if (timelineResult.success) {
        setTimelineStats(timelineResult.data || []);
      }

      // 사용자 활동 통계
      const userResult = await auditLogService.getUserActivityStats(null, cleanFilters);
      if (userResult.success) {
        setUserActivityStats(userResult.data || []);
      }
    } catch (err) {
      console.error('통계 조회 오류:', err);
    } finally {
      setLoading(false);
    }
  };

  const getCategoryLabel = (category) => {
    const labels = {
      'ACCESS_CONTROL': '접근 제어',
      'DATA_PROTECTION': '데이터 보호',
      'SYSTEM_SECURITY': '시스템 보안',
      'AUDIT_MONITORING': '감사 모니터링',
      'USER_MANAGEMENT': '사용자 관리',
      'BUSINESS_LOGIC': '비즈니스 로직',
      'SECURITY_VIOLATION': '보안 위반',
      'BIOMETRIC_AUTH': '생체 인증',
      'MEDICAL_DATA': '의료 데이터',
      'BIOMETRIC_MANAGEMENT': '생체정보 관리'
    };
    return labels[category] || category;
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '-';
    return new Date(timestamp).toLocaleString('ko-KR');
  };

  // 카테고리 통계 차트 데이터
  const getCategoryChartData = () => {
    if (!categoryStats.length) return null;

    const colors = [
      'rgba(255, 99, 132, 0.7)',
      'rgba(54, 162, 235, 0.7)',
      'rgba(255, 206, 86, 0.7)',
      'rgba(75, 192, 192, 0.7)',
      'rgba(153, 102, 255, 0.7)',
      'rgba(255, 159, 64, 0.7)',
      'rgba(199, 199, 199, 0.7)',
      'rgba(83, 102, 255, 0.7)',
      'rgba(255, 99, 255, 0.7)',
      'rgba(99, 255, 132, 0.7)'
    ];

    return {
      labels: categoryStats.map(stat => getCategoryLabel(stat.category)),
      datasets: [{
        label: '이벤트 수',
        data: categoryStats.map(stat => parseInt(stat.event_count)),
        backgroundColor: colors.slice(0, categoryStats.length),
        borderWidth: 1
      }]
    };
  };

  // 시간대별 통계 차트 데이터
  const getTimelineChartData = () => {
    if (!timelineStats.length) return null;

    // 시간 버킷 포맷팅 함수
    const formatTimeBucket = (bucket) => {
      if (!bucket) return '';
      
      switch(timeInterval) {
        case 'hour':
          // "2025-10-25 12:00:00" -> "10/25 12시"
          const hourDate = new Date(bucket);
          return `${hourDate.getMonth() + 1}/${hourDate.getDate()} ${hourDate.getHours()}시`;
          
        case 'day':
          // "2025-10-25" -> "10/25"
          const dayDate = new Date(bucket);
          return `${dayDate.getMonth() + 1}/${dayDate.getDate()}`;
          
        case 'week':
          // "2025-W42" -> "2025년 42주"
          const weekMatch = bucket.match(/(\d{4})-W(\d{2})/);
          if (weekMatch) {
            return `${weekMatch[1]}년 ${parseInt(weekMatch[2])}주`;
          }
          return bucket;
          
        case 'month':
          // "2025-10-01" -> "2025년 10월"
          const monthDate = new Date(bucket);
          return `${monthDate.getFullYear()}년 ${monthDate.getMonth() + 1}월`;
          
        default:
          return bucket;
      }
    };

    return {
      labels: timelineStats.map(stat => formatTimeBucket(stat.time_bucket)),
      datasets: [
        {
          label: '성공',
          data: timelineStats.map(stat => parseInt(stat.success_count)),
          backgroundColor: 'rgba(75, 192, 192, 0.7)'
        },
        {
          label: '실패',
          data: timelineStats.map(stat => parseInt(stat.failure_count)),
          backgroundColor: 'rgba(255, 99, 132, 0.7)'
        }
      ]
    };
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">로딩 중...</span>
        </Spinner>
      </div>
    );
  }

  return (
    <>
      {/* 카테고리 & 시간대별 차트 */}
      <Row className="mb-4">
        <Col md={6}>
          <Card>
            <Card.Header>
              <strong>카테고리별 통계</strong>
            </Card.Header>
            <Card.Body>
              {getCategoryChartData() ? (
                <Doughnut
                  data={getCategoryChartData()}
                  options={{
                    responsive: true,
                    maintainAspectRatio: true,
                    plugins: {
                      legend: {
                        position: 'bottom',
                        labels: {
                          boxWidth: 12,
                          padding: 10,
                          font: {
                            size: 11
                          }
                        }
                      }
                    }
                  }}
                />
              ) : (
                <div className="text-center py-5 text-muted">
                  통계 데이터가 없습니다.
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>

        <Col md={6}>
          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center">
              <strong>시간대별 통계</strong>
              <Form.Select
                style={{ width: '150px' }}
                value={timeInterval}
                onChange={(e) => setTimeInterval(e.target.value)}
              >
                <option value="hour">시간별</option>
                <option value="day">일별</option>
                <option value="week">주별</option>
                <option value="month">월별</option>
              </Form.Select>
            </Card.Header>
            <Card.Body>
              {getTimelineChartData() ? (
                <Bar
                  data={getTimelineChartData()}
                  options={{
                    responsive: true,
                    maintainAspectRatio: true,
                    plugins: {
                      legend: {
                        position: 'bottom',
                        labels: {
                          boxWidth: 12,
                          padding: 10,
                          font: {
                            size: 11
                          }
                        }
                      }
                    },
                    scales: {
                      x: {
                        stacked: true,
                        ticks: {
                          maxRotation: 0,
                          minRotation: 0,
                          autoSkip: true,
                          maxTicksLimit: 20,
                          font: {
                            size: 10
                          }
                        }
                      },
                      y: {
                        stacked: true,
                        beginAtZero: true
                      }
                    }
                  }}
                />
              ) : (
                <div className="text-center py-5 text-muted">
                  통계 데이터가 없습니다.
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* 사용자별 활동 통계 */}
      <Row>
        <Col md={12}>
          <Card>
            <Card.Header>
              <strong>사용자별 활동 통계</strong>
            </Card.Header>
            <Card.Body>
              {userActivityStats.length > 0 ? (
                <div className="table-responsive">
                  <Table hover size="sm">
                    <thead className="table-light">
                      <tr>
                        <th>사용자</th>
                        <th>카테고리</th>
                        <th>총 이벤트</th>
                        <th>성공</th>
                        <th>실패</th>
                        <th>최근 활동</th>
                      </tr>
                    </thead>
                    <tbody>
                      {userActivityStats.map((stat, index) => (
                        <tr key={index}>
                          <td>{stat.username || stat.user_id || '-'}</td>
                          <td>
                            <Badge bg="info" className="text-white">
                              {getCategoryLabel(stat.category)}
                            </Badge>
                          </td>
                          <td>{stat.event_count}</td>
                          <td className="text-success fw-bold">{stat.success_count}</td>
                          <td className="text-danger fw-bold">{stat.failure_count}</td>
                          <td className="text-muted small">
                            {formatTimestamp(stat.last_activity)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              ) : (
                <div className="text-center py-5 text-muted">
                  사용자 활동 통계가 없습니다.
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </>
  );
};

export default AuditLogStatsView;
